import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

export const CONSENT_KEY = "dna_cookie_consent";

export default function CookieBanner() {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(CONSENT_KEY)) setVisible(true);
    } catch {
      setVisible(true);
    }
    const reopen = () => setVisible(true);
    window.addEventListener("dna:cookie-settings", reopen);
    return () => window.removeEventListener("dna:cookie-settings", reopen);
  }, []);

  const decide = (choice) => {
    try {
      localStorage.setItem(CONSENT_KEY, choice);
    } catch {
      /* ignore */
    }
    setVisible(false);
    if (choice === "all" && typeof window.__dnaLoadTrackers === "function") {
      window.__dnaLoadTrackers();
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 left-0 right-0 z-[90] p-4 md:p-6"
          data-testid="cookie-banner"
        >
          <div className="max-w-[900px] mx-auto bg-obsidian-elevated border border-white/15 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)] p-5 md:p-6">
            <div className="flex items-start gap-4">
              <Cookie className="w-6 h-6 text-brand-bright shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-heading font-bold text-white text-base uppercase tracking-wider mb-1.5">
                  {t.cookie.title}
                </p>
                <p className="text-zinc-400 text-xs md:text-sm leading-relaxed">
                  {t.cookie.text}{" "}
                  <Link to="/datenschutz" className="text-brand-bright hover:underline" data-testid="cookie-privacy-link">
                    {t.cookie.more}
                  </Link>
                </p>
                <div className="flex flex-col sm:flex-row gap-2.5 mt-4">
                  <button
                    onClick={() => decide("all")}
                    data-testid="cookie-accept-all"
                    className="bg-brand text-black text-xs font-bold uppercase tracking-wider px-6 py-3 hover:bg-brand-hover transition-colors"
                  >
                    {t.cookie.acceptAll}
                  </button>
                  <button
                    onClick={() => decide("essential")}
                    data-testid="cookie-essential"
                    className="border border-white/25 text-white text-xs font-bold uppercase tracking-wider px-6 py-3 hover:border-brand-light hover:text-brand-bright transition-colors"
                  >
                    {t.cookie.essential}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
